import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  RiAddLine,
  RiMore2Line,
  RiEditLine,
  RiDeleteBinLine,
  RiFileCopyLine,
  RiEyeLine,
  RiBox3Line,
  RiSearchLine,
  RiStarFill,
  RiStarLine,
  RiImageLine,
} from "@remixicon/react";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { getCurrencySymbol as getStoreCurrencySymbol } from "@/utils/currency";
import { Skeleton } from "@/components/ui/skeleton";

interface Product {
  _id: string;
  name: string;
  description: string;
  basePrice: number;
  compareAtPrice?: number;
  category:
    | {
        _id: string;
        name: string;
      }
    | string;
  images: Array<{
    url: string;
    alt?: string;
    isDefault?: boolean;
  }>;
  status: "draft" | "active" | "archived";
  stockStatus: "in_stock" | "low_stock" | "out_of_stock";
  totalStock: number;
  featured: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProductListFilters {
  search: string;
  category: string;
  status: string;
  stockStatus: string;
  featured: string;
}

export function ProductList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentStore, storeId } = useCurrentStore();
  const [filters, setFilters] = useState<ProductListFilters>({
    search: "",
    category: "all",
    status: "all",
    stockStatus: "all",
    featured: "all",
  });
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [deleteProductId, setDeleteProductId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch products
  const { data: productsData, isLoading } = useQuery({
    queryKey: ["products", filters, sortBy, sortOrder, page, storeId],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.search) params.append("search", filters.search);
      if (filters.category !== "all")
        params.append("category", filters.category);
      if (filters.status !== "all") params.append("status", filters.status);
      if (filters.stockStatus !== "all")
        params.append("stockStatus", filters.stockStatus);
      if (filters.featured !== "all")
        params.append("featured", filters.featured);

      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);
      params.append("page", page.toString());
      params.append("limit", "15");

      const { data } = await api.get(`/api/products?${params}`);
      return data;
    },
  });

  // Fetch categories for filter
  const { data: categories = [] } = useQuery({
    queryKey: ["categories", storeId],
    queryFn: async () => {
      const { data } = await api.get("/api/categories?tree=true");
      return data;
    },
  });

  // Flatten hierarchical categories for display
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flattenCategories = (
    cats: any[]
  ): Array<{ _id: string; name: string; level: number }> => {
    const result: Array<{ _id: string; name: string; level: number }> = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flatten = (categories: any[], level = 0) => {
      categories.forEach((cat) => {
        result.push({ _id: cat._id, name: cat.name, level });
        if (cat.children && cat.children.length > 0) {
          flatten(cat.children, level + 1);
        }
      });
    };
    flatten(cats);
    return result;
  };

  const flatCategories = flattenCategories(categories);

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      return api.delete(`/api/products/${productId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product deleted successfully");
      setDeleteProductId(null);
      setShowDeleteDialog(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete product");
    },
  });

  // Toggle featured mutation
  const toggleFeaturedMutation = useMutation({
    mutationFn: async ({
      productId,
      featured,
    }: {
      productId: string;
      featured: boolean;
    }) => {
      return api.patch(`/api/products/${productId}`, { featured });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product updated successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update product");
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      productId,
      status,
    }: {
      productId: string;
      status: "draft" | "active" | "archived";
    }) => {
      return api.patch(`/api/products/${productId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product status updated");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update status");
    },
  });

  // Duplicate product mutation
  const duplicateProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      return api.post(`/api/products/${productId}/duplicate`);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product duplicated successfully");
      // Navigate to the new duplicated product for editing
      //navigate(`/dashboard/products/${response.data._id}`);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to duplicate product"
      );
    },
  });

  const handleChangeStatus = (
    productId: string,
    status: "draft" | "active" | "archived"
  ) => {
    updateStatusMutation.mutate({ productId, status });
  };

  const handleFilterChange = (key: keyof ProductListFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const getStockBadge = (stockStatus: string, totalStock: number) => {
    switch (stockStatus) {
      case "in_stock":
        return (
          <Badge variant="secondary" className="rounded-full border-0 bg-emerald-50 text-emerald-700">
            In Stock ({totalStock})
          </Badge>
        );
      case "low_stock":
        return (
          <Badge variant="secondary" className="rounded-full border-0 bg-amber-50 text-amber-700">
            Low Stock ({totalStock})
          </Badge>
        );
      case "out_of_stock":
        return (
          <Badge variant="secondary" className="rounded-full border-0 bg-red-50 text-red-700">
            Out of Stock
          </Badge>
        );
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge variant="secondary" className="rounded-full border-0 bg-emerald-50 text-emerald-700">
            Active
          </Badge>
        );
      case "draft":
        return <Badge variant="outline">Draft</Badge>;
      case "archived":
        return (
          <Badge variant="secondary" className="text-gray-600 bg-gray-50">
            Archived
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCurrencySymbol = () => {
    return getStoreCurrencySymbol(currentStore);
  };

  const formatProductPrice = (price: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: currentStore?.currency || "NGN",
    }).format(price);
  };

  const handleDeleteProduct = (productId: string) => {
    setDeleteProductId(productId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (deleteProductId) {
      deleteProductMutation.mutate(deleteProductId);
    }
  };

  const handleToggleFeatured = (product: Product) => {
    toggleFeaturedMutation.mutate({
      productId: product._id,
      featured: !product.featured,
    });
  };

  const handleDuplicateProduct = (productId: string) => {
    duplicateProductMutation.mutate(productId);
  };

  const products = productsData?.products || [];
  const pagination = productsData?.pagination || {
    page: 1,
    pages: 1,
    total: 0,
  };

  useEffect(() => {
    if (!showDeleteDialog) {
      setDeleteProductId(null);
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [showDeleteDialog]);

  return (
    <div className="space-y-6">
      <SectionCard>
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            <div className="relative flex-1 min-w-0">
              <RiSearchLine className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className={dashboardStyles.filterInput}
              />
            </div>

            <div className="flex flex-wrap gap-2 flex-shrink-0">
             
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange("status", value)}
              >
                <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-auto min-w-[120px] max-w-[160px]`}>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              
              <Select
                value={filters.category}
                onValueChange={(value) => handleFilterChange("category", value)}
              >
                <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-auto min-w-[140px] max-w-[200px]`}>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {flatCategories.map((category) => (
                    <SelectItem key={category._id} value={category._id}>
                      {"— ".repeat(category.level)}
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
             
            </div>
          </div>
          {isLoading ? (
            isMobile ? (
              // Mobile loading skeleton
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className={dashboardStyles.listItem}>
                    <div className="flex items-center gap-3 w-full">
                      <Skeleton className="w-14 h-14 rounded-lg flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <Skeleton className="h-4 w-36" />
                          <Skeleton className="h-7 w-7 rounded-md" />
                        </div>
                        <Skeleton className="h-3 w-52 mb-2.5" />
                        <div className="flex gap-2">
                          <Skeleton className="h-5 w-16 rounded-full" />
                          <Skeleton className="h-5 w-20 rounded-full" />
                          <Skeleton className="h-5 w-14 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop loading skeleton
              <div className={dashboardStyles.tableWrap}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Stock</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton className="h-4 w-4 rounded" />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Skeleton className="w-11 h-11 rounded-lg flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <Skeleton className="h-4 w-36 mb-1.5" />
                              <Skeleton className="h-3 w-52" />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-5 w-24 rounded-full" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-5 w-20 rounded-full" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-7 w-7 rounded-md" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 rounded-xl bg-muted/20">
              <div className={dashboardStyles.emptyIcon}>
                <RiBox3Line className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className={dashboardStyles.emptyTitle}>
                {filters.search || filters.category !== "all" || filters.status !== "all"
                  ? "No products found"
                  : "No products yet"}
              </p>
              <p className={dashboardStyles.emptyDescription}>
                {filters.search || filters.category !== "all" || filters.status !== "all"
                  ? "Try adjusting your filters or search terms to find what you're looking for."
                  : "Start building your catalog by adding your first product."}
              </p>
              {!filters.search &&
                filters.category === "all" &&
                filters.status === "all" && (
                  <Button onClick={() => navigate("/dashboard/products/new")} size="sm" className="h-9 rounded-full px-4 text-xs font-semibold shadow-none">
                    <RiAddLine className="w-4 h-4" />
                    Add Product
                  </Button>
                )}
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              {isMobile ? (
                <div className="space-y-3">
                  {products.map((product: Product) => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      onEdit={(productId) => navigate(`/dashboard/products/${productId}`)}
                      onDelete={handleDeleteProduct}
                      onChangeStatus={handleChangeStatus}
                      onDuplicate={handleDuplicateProduct}
                      onToggleFeatured={handleToggleFeatured}
                      getStatusBadge={getStatusBadge}
                      getStockBadge={getStockBadge}
                      formatProductPrice={formatProductPrice}
                    />
                  ))}
                </div>
              ) : (
                /* Desktop Table View */
                <div className={dashboardStyles.tableWrap}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead
                          className="cursor-pointer"
                          onClick={() => handleSort("name")}
                        >
                          Product
                        </TableHead>
                        <TableHead
                          className="cursor-pointer"
                          onClick={() => handleSort("status")}
                        >
                          Status
                        </TableHead>
                        <TableHead>Stock</TableHead>
                        <TableHead
                          className="cursor-pointer"
                          onClick={() => handleSort("basePrice")}
                        >
                          Price
                        </TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product: Product) => (
                        <TableRow key={product._id}>
                          <TableCell>
                            {product.featured && (
                              <RiStarFill className="w-4 h-4 text-yellow-500" />
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {product.images && product.images.length > 0 ? (
                                <img
                                  src={product.images[0].url}
                                  alt={product.name}
                                  className="w-12 h-12 object-cover rounded-lg border"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-100 rounded-lg border flex items-center justify-center">
                                  <RiImageLine className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1 max-w-[150px]">
                                <p className="font-medium text-gray-900 truncate">
                                  {product.name}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                  {product.description}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(product.status)}</TableCell>
                          <TableCell>
                            {getStockBadge(
                              product.stockStatus,
                              product.totalStock
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {formatProductPrice(product.basePrice)}
                              </span>
                              {product.compareAtPrice &&
                                product.compareAtPrice > product.basePrice && (
                                  <span className="text-sm text-gray-500 line-through">
                                    {formatProductPrice(product.compareAtPrice)}
                                  </span>
                                )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {typeof product.category === "object" &&
                            product.category ? (
                              <Badge variant="outline">
                                {product.category.name}
                              </Badge>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon-sm" className="rounded-full">
                                  <RiMore2Line className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/dashboard/products/${product._id}`)
                                  }
                                >
                                  <RiEditLine className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>
                                    Status: {product.status}
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleChangeStatus(product._id, "active")
                                      }
                                      disabled={product.status === "active"}
                                    >
                                      Set Active
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleChangeStatus(product._id, "draft")
                                      }
                                      disabled={product.status === "draft"}
                                    >
                                      Set Draft
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleChangeStatus(
                                          product._id,
                                          "archived"
                                        )
                                      }
                                      disabled={product.status === "archived"}
                                    >
                                      Set Archived
                                    </DropdownMenuItem>
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleDuplicateProduct(product._id)
                                  }
                                >
                                  <RiFileCopyLine className="w-4 h-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDeleteProduct(product._id)}
                                  className="text-red-600"
                                >
                                  <RiDeleteBinLine className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className={dashboardStyles.paginationBar}>
                  <p>
                    Showing page {pagination.page} of {pagination.pages} (
                    {pagination.total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className={dashboardStyles.paginationBtn}
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={dashboardStyles.paginationBtn}
                      onClick={() => setPage(page + 1)}
                      disabled={page === pagination.pages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
      </SectionCard>

      <DeleteProductModal
        isOpen={showDeleteDialog}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

// Product Card Component for Mobile View
const ProductCard = ({ 
  product, 
  onEdit, 
  onDelete, 
  onChangeStatus, 
  onDuplicate, 
  onToggleFeatured,
  getStatusBadge,
  getStockBadge,
  formatProductPrice 
}: {
  product: Product;
  onEdit: (productId: string) => void;
  onDelete: (productId: string) => void;
  onChangeStatus: (productId: string, status: "draft" | "active" | "archived") => void;
  onDuplicate: (productId: string) => void;
  onToggleFeatured: (product: Product) => void;
  getStatusBadge: (status: string) => React.ReactNode;
  getStockBadge: (stockStatus: string, totalStock: number) => React.ReactNode;
  formatProductPrice: (price: number) => string;
}) => {
  return (
    <div className={dashboardStyles.listItem}>
      <div className="flex items-center gap-3 w-full">
          {/* Product Image */}
          <div className="flex-shrink-0">
            {product.images && product.images.length > 0 ? (
              <img
                src={product.images[0].url}
                alt={product.name}
                className="w-14 h-14 object-cover rounded-md border"
              />
            ) : (
              <div className="w-14 h-14 bg-gray-100 rounded-md border flex items-center justify-center">
                <RiImageLine className="w-6 h-6 text-gray-400" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-gray-900 truncate text-sm">
                    {product.name}
                  </h3>
                  {product.featured && (
                    <RiStarFill className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-gray-500 line-clamp-1 mb-1">
                  {product.description}
                </p>
              </div>
              
              {/* Actions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    <RiMore2Line className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(product._id)}>
                    <RiEditLine className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      Status: {product.status}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onClick={() => onChangeStatus(product._id, "active")}
                        disabled={product.status === "active"}
                      >
                        Set Active
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onChangeStatus(product._id, "draft")}
                        disabled={product.status === "draft"}
                      >
                        Set Draft
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onChangeStatus(product._id, "archived")}
                        disabled={product.status === "archived"}
                      >
                        Set Archived
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuItem onClick={() => onDuplicate(product._id)}>
                    <RiFileCopyLine className="w-4 h-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(product._id)}
                    className="text-red-600"
                  >
                    <RiDeleteBinLine className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Status, Stock, Price, and Category in one row */}
            <div className="flex items-start sm:items-center justify-between gap-2 mt-2 flex-col sm:flex-row">
              <div className="flex items-center gap-2 flex-1 flex-wrap">
                {getStatusBadge(product.status)}
                {getStockBadge(product.stockStatus, product.totalStock)}
                {typeof product.category === "object" && product.category ? (
                  <Badge variant="outline" className="text-xs">
                    {product.category.name}
                  </Badge>
                ) : (
                  <span className="text-gray-400 text-xs">—</span>
                )}
              </div>
              
              {/* Price */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="font-semibold text-sm">
                  {formatProductPrice(product.basePrice)}
                </span>
                {product.compareAtPrice && product.compareAtPrice > product.basePrice && (
                  <span className="text-xs text-gray-500 line-through">
                    {formatProductPrice(product.compareAtPrice)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

const DeleteProductModal = ({
  isOpen,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 font-heading">Delete Product</h2>
          <p className="mt-2 text-sm text-gray-500">
            Are you sure you want to delete this product? This action cannot be
            undone. All product data, including images and variants, will be
            permanently removed.
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="bg-red-600 text-white hover:bg-red-700"
            size="sm"
            onClick={onConfirm}
          >
            Delete Product
          </Button>
        </div>
      </div>
    </div>
  );
};
